<div class="report-actions">
    <button type="button" class="secondary" (click)="printReport()">
        <span class="material-symbols-rounded leading">print</span>
        {{ 'FEATURES.MEASUREMENTS.REPORT_ACTIONS.PRINT_REPORT' | translate }}
    </button>
    <button type="button" class="secondary" (click)="downloadReportPDF()" [disabled]="downloading.pdf">
        <span class="material-symbols-rounded leading" [class.spinner]="downloading.pdf">{{
            downloading.pdf ? 'progress_activity' : 'download'
        }}</span>
        {{
            (downloading.pdf
                ? 'FEATURES.MEASUREMENTS.REPORT_ACTIONS.GENERATING'
                : 'FEATURES.MEASUREMENTS.REPORT_ACTIONS.DOWNLOAD_PDF'
            ) | translate
        }}
    </button>
    <button type="button" class="secondary" (click)="exportToExcel()" [disabled]="downloading.excel">
        <span class="material-symbols-rounded leading" [class.spinner]="downloading.excel">{{
            downloading.excel ? 'progress_activity' : 'table_view'
        }}</span>
        {{
            (downloading.excel
                ? 'FEATURES.MEASUREMENTS.REPORT_ACTIONS.EXPORTING'
                : 'FEATURES.MEASUREMENTS.REPORT_ACTIONS.EXPORT_EXCEL'
            ) | translate
        }}
    </button>
    <button type="button" class="secondary" (click)="exportToCSV()" [disabled]="downloading.csv">
        <span class="material-symbols-rounded leading" [class.spinner]="downloading.csv">{{
            downloading.csv ? 'progress_activity' : 'description'
        }}</span>
        {{
            (downloading.csv
                ? 'FEATURES.MEASUREMENTS.REPORT_ACTIONS.EXPORTING'
                : 'FEATURES.MEASUREMENTS.REPORT_ACTIONS.EXPORT_CSV'
            ) | translate
        }}
    </button>
    <button
        *appHasPermissions="[permissions.InternalUser]"
        type="button"
        class="secondary"
        (click)="downloadRawData()"
        [disabled]="downloading.raw"
    >
        <span class="material-symbols-rounded leading" [class.spinner]="downloading.raw">{{
            downloading.raw ? 'progress_activity' : 'code'
        }}</span>
        {{
            (downloading.raw
                ? 'FEATURES.MEASUREMENTS.REPORT_ACTIONS.EXPORTING'
                : 'FEATURES.MEASUREMENTS.REPORT_ACTIONS.DOWNLOAD_RAW_DATA'
            ) | translate
        }}
    </button>
</div>

<div class="report-content" #reportContent>
    <div class="report-header">
        <h2>{{ 'FEATURES.MEASUREMENTS.REPORT_ACTIONS.REPORT_TITLE' | translate }}</h2>
    </div>

    <div class="report-section">
        <h3>{{ 'FEATURES.MEASUREMENTS.REPORT_ACTIONS.SECTIONS.GENERAL' | translate }}</h3>
        <table class="report-table">
            <thead>
                <tr>
                    <th>{{ 'FEATURES.MEASUREMENTS.REPORT_ACTIONS.TABLE_HEADERS.START_DATE' | translate }}</th>
                    <th>{{ 'FEATURES.MEASUREMENTS.REPORT_ACTIONS.TABLE_HEADERS.DURATION' | translate }}</th>
                    <th>{{ 'FEATURES.MEASUREMENTS.REPORT_ACTIONS.TABLE_HEADERS.FRAME_COUNT' | translate }}</th>
                    <th>{{ 'FEATURES.MEASUREMENTS.REPORT_ACTIONS.TABLE_HEADERS.REFERENCE' | translate }}</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>{{ getFormattedDate() }} {{ measurementData?.meta?.time || '-' }}</td>
                    <td>{{ measurementData?.meta?.duration | duration: 'time' }}</td>
                    <td>{{ getFrameCount() }}</td>
                    <td>{{ measurementData?.meta?.ref || '-' }}</td>
                </tr>
            </tbody>
        </table>

        <table class="report-table">
            <thead>
                <tr>
                    <th>{{ 'FEATURES.MEASUREMENTS.REPORT_ACTIONS.TABLE_HEADERS.SERIAL_NUMBER' | translate }}</th>
                    <th>{{ 'FEATURES.MEASUREMENTS.REPORT_ACTIONS.TABLE_HEADERS.GENERATION' | translate }}</th>
                    <th>{{ 'FEATURES.MEASUREMENTS.REPORT_ACTIONS.TABLE_HEADERS.VERSION' | translate }}</th>
                    <th>{{ 'FEATURES.MEASUREMENTS.REPORT_ACTIONS.TABLE_HEADERS.ANNOTATION' | translate }}</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>{{ measurementData?.meta?.serial || '-' }}</td>
                    <td>{{ measurementData?.meta?.device || '-' }}</td>
                    <td>{{ measurementData?.meta?.softwareVersion || '-' }}</td>
                    <td>{{ getAnnotation() }}</td>
                </tr>
            </tbody>
        </table>

        <table class="report-table">
            <thead>
                <tr>
                    <th>{{ 'FEATURES.SCORECARD.METRICS.MONITOR_UNTIL_ACCEPTABLE_RECOVERY' | translate }}</th>
                    <th>{{ 'FEATURES.SCORECARD.METRICS.SIGNAL_INTEGRITY' | translate }}</th>
                    <th>{{ 'FEATURES.SCORECARD.METRICS.START_PRE_PARALYTIC' | translate }}</th>
                    <th>{{ 'FEATURES.SCORECARD.METRICS.PTC_USE' | translate }}</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>{{ getYesOrNo(measurementData?.dynamicData?.monitorUntilAcceptableRecovery) }}</td>
                    <td>{{ getYesOrNo(measurementData?.dynamicData?.goodSignalStrength) }}</td>
                    <td>{{ getYesOrNo(measurementData?.dynamicData?.startPreParalytic) }}</td>
                    <td>{{ getYesOrNo(measurementData?.dynamicData?.usedPTCProperly) }}</td>
                </tr>
            </tbody>
        </table>
    </div>

    <div class="report-section">
        <h3>{{ 'FEATURES.MEASUREMENTS.REPORT_ACTIONS.SECTIONS.DEVICE_SETTINGS' | translate }}</h3>
        <table class="report-table">
            <thead>
                <tr>
                    <th>{{ 'FEATURES.MEASUREMENTS.REPORT_ACTIONS.TABLE_HEADERS.AUTO_MANUAL' | translate }}</th>
                    <th>{{ 'FEATURES.MEASUREMENTS.REPORT_ACTIONS.TABLE_HEADERS.SUPRA_MAXIMAL' | translate }}</th>
                    <th>{{ 'FEATURES.MEASUREMENTS.REPORT_ACTIONS.TABLE_HEADERS.CURRENT_MA' | translate }}</th>
                    <th>{{ 'FEATURES.MEASUREMENTS.REPORT_ACTIONS.TABLE_HEADERS.PULSE_WIDTH' | translate }}</th>
                    <th>
                        {{ 'FEATURES.MEASUREMENTS.REPORT_ACTIONS.TABLE_HEADERS.INTERVAL' | translate }}
                        {{ getIntervalUnit() }}
                    </th>
                    <th>{{ 'FEATURES.MEASUREMENTS.REPORT_ACTIONS.TABLE_HEADERS.FILTER_HZ' | translate }}</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>{{ getAutoMode() }}</td>
                    <td>{{ getSupramaximal() }}</td>
                    <td>{{ getStimulationCurrent() }}</td>
                    <td>{{ getPulseWidth() }}</td>
                    <td>{{ getInterval() }}</td>
                    <td>{{ measurementData?.meta?.filter || '-' }}</td>
                </tr>
            </tbody>
        </table>
    </div>

    <div class="report-section">
        <h3>{{ 'FEATURES.MEASUREMENTS.REPORT_ACTIONS.SECTIONS.SUMMARY' | translate }}</h3>
        <div class="report-graph">
            <app-trend-graph [settings]="controlSettings" [staticMode]="true"></app-trend-graph>
        </div>
    </div>
</div>
